import React, { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import shortid from 'shortid'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'
import { useStopBodyScroll } from 'hooks/useScrollEffects'
import useKeylistener from 'hooks/useKeylistener'

import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { Option } from 'components/elements/FloatingToolbar/types'

import Icon from 'components/elements/Icon'
import FloatingToolbar from 'components/elements/FloatingToolbar'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import styles from './globalOverlayStack.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  mobileToolbarOptions?: Option[]
  rootClassName?: string
  backButtonText?: string
  remainMounted?: boolean
  id?: string
  noWeirdClose?: boolean
}

const TRANSITION_TIME = 200 // Should match the transition time in the CSS

const GlobalOverlayStack = forwardRef(
  (
    {
      open,
      onClose,
      children,
      className = '',
      mobileToolbarOptions = [],
      rootClassName = '',
      remainMounted,
      noWeirdClose = false,
      id: parentId,
    }: Props,
    parentRef: any,
  ) => {
    if (typeof document === 'undefined') return null

    const id = useRef(parentId || shortid.generate())

    const overlayRef = useRef<HTMLDivElement | null>(null)
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)

    const startingTouchPosition = useRef<[number, number] | null>(null)
    const dragOffset = useRef<[number, number] | null>(null)
    const startingOffset = useRef<number | null>(null)

    const [swipeAwayOffset, setSwipeAwayOffset] = useState(0)

    const [ready, setReady] = useState(false) // use this rather than open. Open is used from the parent, but we need to append the ID to the stackOverlayIds before we can show the content
    const [visable, setVisable] = useState(false)
    const [contentVisable, setContentVisable] = useState(false) // used to transition the content, not the background
    const [closeClickStarted, setCloseClickStarted] = useState(false)
    const [leaving, setLeaving] = useState(false)

    const setNoScroll = useStopBodyScroll(open)

    const [isActiveStackElement, setIsActiveStackElement] = useState(false)

    const stackOverlayIds = useAppSelector(state => state.active.stackOverlayIds)
    const hideStackOverlay = useAppSelector(state => state.active.hideStackOverlay)

    const [setActiveState] = useActionless(SET_ACTIVE_STATE)

    useKeylistener(
      (e: KeyboardEvent) => {
        if (!onClose || !open) return

        if (basicKeybindSkips(e)) return

        if (e.key === 'Escape') {
          e.preventDefault() // prevents exiting fullscreen on macos (I think)
          onClose()
        }
      },
      [open],
    )

    useEffect(() => {
      if (!open && !visable) return

      if (open) {
        setNoScroll(true)
        setActiveState({ stackOverlayIds: [...stackOverlayIds, id.current] })
        setReady(true)

        setVisable(true)
        setContentVisable(true)
        setSwipeAwayOffset(0)

        return
      }

      setNoScroll(false)
      setVisable(false)
      setContentVisable(false)

      if (!swipeAwayOffset) setLeaving(true)
      else setSwipeAwayOffset(0)

      setTimeout(() => {
        setReady(false)
        setLeaving(false)
        setSwipeAwayOffset(0)
        setActiveState({ stackOverlayIds: stackOverlayIds.filter(overlayId => overlayId !== id.current) })
      }, TRANSITION_TIME)
    }, [open])

    // Handle hiding self if another overlay appears
    useEffect(() => {
      if (!open && !visable) return

      const isActiveStackElement = stackOverlayIds[stackOverlayIds.length - 1] === id.current

      if (isActiveStackElement) {
        setIsActiveStackElement(isActiveStackElement)
        setContentVisable(true)

        return
      }

      setIsActiveStackElement(isActiveStackElement)
      setContentVisable(false)
    }, [stackOverlayIds])

    // Cleanup
    useEffect(() => {
      // Regardless of the components state when this component is unmounted
      // Check the store, and remove the ID from the stack if it still exists
      // This is a failsafe to be sure the ID is removed from the stack even if the element is removed via unorthodox means
      return () => {
        const currentIds = store.getState().active.stackOverlayIds
        const stillInStack = store.getState().active.stackOverlayIds.includes(id.current)

        // If after we
        if (stillInStack) {
          setActiveState({ stackOverlayIds: currentIds.filter(overlayId => overlayId !== id.current) })
        }
      }
    }, [])

    // Using the built in onClick means that if you start the click in the modal and end it in the dimmer area it closes
    // the modal. This is frustrating when selecting text in an input field. To avoid this we track if the click started
    // in the dimmer area and only close the modal if it did and it also ended in the dimmer area.
    const handleWeirdCloseMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (noWeirdClose) return
      if (e.clientX >= e.currentTarget.clientWidth) return // Skip if they clicked on a scrollbar
      if (e.target === e.currentTarget) setCloseClickStarted(true)
      else setCloseClickStarted(false)
    }
    const handleWeirdCloseMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
      if (noWeirdClose) return
      if (e.target === e.currentTarget && closeClickStarted) onClose && onClose()
      setCloseClickStarted(false)
    }

    const handleSetOffset = (desiredOffset: number) => {
      if (desiredOffset < 0) return setSwipeAwayOffset(0)

      const offsetPercentage = desiredOffset / document.documentElement.clientWidth

      setSwipeAwayOffset(offsetPercentage)
    }

    const handleTouchStart = (touchEvent: React.TouchEvent) => {
      startingTouchPosition.current = [touchEvent.touches[0]?.clientX, touchEvent.touches[0]?.clientY]
      startingOffset.current = swipeAwayOffset
    }

    const handleTouch = (touchEvent: React.TouchEvent) => {
      if (startingTouchPosition.current === null || startingOffset.current === null) return

      dragOffset.current = [
        touchEvent.touches[0]?.clientX - startingTouchPosition.current[0],
        touchEvent.touches[0]?.clientY - startingTouchPosition.current[1],
      ]

      // Prevents misfires - don't start listening to drags until we're past our jitter boundry
      // Also allows internal scrolling of elements since it doesn't disable scrolling w/ the .activlyDragging class until we're off of openBodyScroll.scrollTop === 0
      if (dragOffset.current[0] < 50) return

      // If the user started scrolling Y ignore this as a side-to-side swipe
      if (Math.abs(dragOffset.current[1]) > 50) return

      // overlayRef.current?.classList.add(styles.activelyDragging)

      handleSetOffset(dragOffset.current[0] - 50)
    }

    const handleTouchEnd = () => {
      if (swipeAwayOffset > 0.1) {
        handleSetOffset(document.body.clientWidth)

        setTimeout(() => {
          setNoScroll(false)
          setVisable(false)
          setContentVisable(false)
          setReady(false)
          setLeaving(false)
          setActiveState({ stackOverlayIds: stackOverlayIds.filter(overlayId => overlayId !== id.current) })

          onClose()
        }, TRANSITION_TIME)

        return
      }

      handleSetOffset(0)
      handleResetDraggingState()
    }

    const handleResetDraggingState = () => {
      overlayRef.current?.classList.remove(styles.activelyDragging)

      // Always reset state once the drawer has entirely opened/ shut
      dragOffset.current = null
      startingOffset.current = null
      startingTouchPosition.current = null
    }

    if (!ready && !visable && !remainMounted) return null

    const isRootComponent = stackOverlayIds[0] === id.current
    const isTopmostComponent = stackOverlayIds[stackOverlayIds.length - 1] === id.current

    const swippingAwayContent: CSSProperties = {
      opacity: swipeAwayOffset > 0 ? 1 - swipeAwayOffset : undefined,
      transition: swipeAwayOffset === 1 ? 'opacity 1s ease' : undefined,
    }

    return ReactDOM.createPortal(
      <div
        id={id.current}
        onTouchStart={isTopmostComponent ? handleTouchStart : undefined}
        onTouchMove={isTopmostComponent ? handleTouch : undefined}
        onTouchEnd={isTopmostComponent ? handleTouchEnd : undefined}
        style={{
          transform: `translateX(${swipeAwayOffset * 100}%)`,
          animation: swipeAwayOffset > 0 ? 'unset' : undefined,
        }}
        ref={overlayRef}
        className={`
          ${styles.overlay}
          ${isRootComponent ? styles.rootOverlay : ''}
          ${visable && !hideStackOverlay ? styles.overlayVisable : ''}
          ${leaving ? styles.overlayInactive : ''}
          ${rootClassName}
        `}>
        <div
          ref={el => {
            if (parentRef) parentRef.current = el

            setContainerRef(el)
          }}
          onMouseDown={handleWeirdCloseMouseDown}
          onMouseUp={handleWeirdCloseMouseUp}
          style={swippingAwayContent}
          className={`
          ${styles.content}
          ${contentVisable && !hideStackOverlay ? styles.visable : ''}
          ${leaving || !isActiveStackElement ? styles.inactive : ''}
        `}>
          <div className={`${styles.primaryContent} ${className}`}>{children}</div>
        </div>

        <div
          style={swippingAwayContent}
          className={`${styles.desktopNavigation} ${contentVisable ? styles.visable : ''} ${!isActiveStackElement ? styles.inactive : ''}`}>
          <button onClick={onClose}>
            <Icon name={isRootComponent ? 'close' : 'arrow left'} />
            <span>{isRootComponent ? 'Close' : 'Back'}</span>
          </button>
        </div>

        <FloatingToolbar
          hideOnScrollRef={containerRef}
          options={[
            {
              icon: isRootComponent ? 'close' : 'arrow left',
              label: isRootComponent ? 'Close' : 'Back',
              onClick: onClose,
            },
            ...mobileToolbarOptions,
          ]}
          style={swippingAwayContent}
          className={`
            ${styles.mobileToolbar}
            ${contentVisable && !hideStackOverlay ? styles.visable : ''}
            ${!isActiveStackElement ? styles.inactive : ''}
          `}
        />

        {isRootComponent && <div className={styles.grid} />}
      </div>,
      document.body,
    )
  },
)

export default GlobalOverlayStack
